<template>
    <validation-observer ref="form" v-slot="{ meta, handleSubmit }">
        <modal
            :id="modalId"
            title="Widget Settings"
            scrollable
            ok-title="Save"
            :ok-disabled="!meta.valid || loading || disabledSubmit"
            @ok.prevent="handleSubmit(_, save)"
            @hide="hideModal"
            @shown="setData"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form @submit.prevent="handleSubmit(_, save)" v-if="values">
                <b-row>
                    <b-col>
                        <validation-provider name="settings.name" v-slot="{ errors }" >
                            <form-input label="Name:" v-model="values.settings.name" :error="errors[0]"></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="withAirbnbReservations">
                    <b-col>
                        <form-checkbox v-model="values.settings.display_airbnb_reservations">
                            Display Airbnb Reservations
                        </form-checkbox>
                    </b-col>
                </b-row>
                <b-row v-if="withHideOlderThanDays">
                    <b-col>
                        <validation-provider name="settings.hide_older_than_days" v-slot="{ errors }" >
                            <form-select
                                label="Hide reservations older than:"
                                v-model="values.settings.hide_older_than_days"
                                :options="hideOlderThanDaysOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="withBlockedReservations">
                    <b-col>
                        <form-checkbox v-model="values.settings.display_blocked_reservations">
                            Display Owner/iCal blocks
                        </form-checkbox>
                    </b-col>
                </b-row>
                <b-row v-if="withChartModule">
                    <b-col>
                        <validation-provider name="settings.chart_module" v-slot="{ errors }" >
                            <form-select
                                label="Module:"
                                v-model="values.settings.chart_module"
                                :options="chartModuleOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="withChartInterval">
                    <b-col>
                        <validation-provider name="settings.chart_interval" v-slot="{ errors }" >
                            <form-select
                                label="Data Points:"
                                v-model="values.settings.chart_interval"
                                @change="onIntervalChange"
                                :options="chartIntervalOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="withChartPeriod">
                    <b-col>
                        <validation-provider name="settings.chart_period" v-slot="{ errors }" >
                            <form-select
                                label="Period:"
                                v-model="values.settings.chart_period"
                                :options="chartPeriodOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="withTableFontSize">
                    <b-col>
                        <validation-provider name="settings.table_font_size" v-slot="{ errors }" >
                            <form-select
                                label="Table Font Size:"
                                v-model="values.settings.table_font_size"
                                :options="tableFontSizeOptions"
                                :error="errors[0]"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <div v-if="withCoords">
                    <hr />
                    <form-select
                        v-model="values.settings.weather_unit"
                        :options="weatherUnits"
                        label="Units:"
                        class="w-100"
                    />
                    <div class="d-flex flex-md-row flex-column justify-content-center">
                        <form-select
                            v-model="selectedCountry"
                            :options="countryList"
                            @change="onCountryChange"
                            label="Country:"
                            class="w-100"
                            :class="{ 'mr-3': hasStates }"
                        />
                        <form-select
                            v-if="hasStates"
                            v-model="selectedState"
                            @change="onStateChange"
                            :options="stateList"
                            label="State:"
                            class="w-100"
                        />
                    </div>
                    <div class="search-city-wrapper">
                        <validation-provider name="city" >
                            <form-input
                                id="city"
                                @input="(value) => fetchCities(value)"
                                label="Enter your city:"
                                v-model="cityName"
                                placeholder="City:"
                                autocomplete="off"
                            />
                        </validation-provider>
                        <div class="city-list-wrapper" v-if="cities.length && cityName">
                            <div
                                class="p-2"
                                :class="{ 'selectable-guest-item': city.id }"
                                :key="city.id"
                                v-for="city in cities"
                                @click="selectCity(city)"
                            >
                                <span>{{ city.name }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
    import API from '../../../../api';
    import { APP_CITY_LIST_ENDPOINT, APP_COUNTRY_LIST_ENDPOINT, APP_STATE_LIST_ENDPOINT } from '../../../../endpoints';
    import {
        DASHBOARD_CHART_INTERVAL_DAILY,
        DASHBOARD_CHART_INTERVAL_MONTHLY,
        DASHBOARD_CHART_INTERVAL_YEARLY,
        DASHBOARD_CHART_INTERVAL_MAPPING,
        DASHBOARD_CHART_MODULE_OWNERS,
        DASHBOARD_CHART_MODULE_QUICKBOOKS,
        DASHBOARD_CHART_MODULE_MAPPING,
    } from '../../../../constants';
    import range from 'lodash/range';
    import isEmpty from 'lodash/isEmpty';
    import debounce from 'lodash/debounce';
    import get from 'lodash/get';
    import { formatResponseError } from '../../../../utils/api';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import FormSelect from '../../../generic/form-select/form-select.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import {
        DEFAULT_DASHBOARD_CHART_INTERVAL,
        DEFAULT_DASHBOARD_CHART_MODULE,
        getDefaultChartPeriodBasedOnInterval,
        weatherUnits,
        STANDARD,
        IMPERIAL,
        METRIC,
    } from '../helpers';
    import FormCheckbox from '../../../generic/form-checkbox/form-checkbox.vue';

    export default {
        name: 'TabWidgetSettingsModalForm',
        components: {
            BusyModal,
            FormInput,
            FormSelect,
            Modal,
            FormCheckbox,
        },
        props: {
            city: Object,
            modalId: String,
            update: {
                type: Function,
                default: () => Promise.resolve(),
            },
            initialValues: {
                type: Object,
                default: () => ({}),
            },
            withChartPeriod: {
                type: Boolean,
                default: false,
            },
            withChartInterval: {
                type: Boolean,
                default: false,
            },
            withChartModule: {
                type: Boolean,
                default: false,
            },
            withAirbnbReservations: {
                type: Boolean,
                default: false,
            },
            withHideOlderThanDays: {
                type: Boolean,
                default: false,
            },
            withBlockedReservations: {
                type: Boolean,
                default: false,
            },
            withCoords: {
                type: Boolean,
                default: false,
            },
            withYearlyChartInterval: {
                type: Boolean,
                default: true,
            },
            withDailyChartInterval: {
                type: Boolean,
                default: true,
            },
            defaultPeriod: Number,
            defaultInterval: String,
            withTableFontSize: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            isYearlyChartInterval() {
                return get(this.values, 'settings.chart_interval') === DASHBOARD_CHART_INTERVAL_YEARLY;
            },
            isMonthlyChartInterval() {
                return get(this.values, 'settings.chart_interval') === DASHBOARD_CHART_INTERVAL_MONTHLY;
            },
            chartIntervalOptions() {
                const interval = [];
                if (this.withDailyChartInterval) {
                    interval.push({
                        value: DASHBOARD_CHART_INTERVAL_DAILY,
                        text: DASHBOARD_CHART_INTERVAL_MAPPING[DASHBOARD_CHART_INTERVAL_DAILY],
                    });
                }
                interval.push({
                    value: DASHBOARD_CHART_INTERVAL_MONTHLY,
                    text: DASHBOARD_CHART_INTERVAL_MAPPING[DASHBOARD_CHART_INTERVAL_MONTHLY],
                });
                if (this.withYearlyChartInterval) {
                    interval.push({
                        value: DASHBOARD_CHART_INTERVAL_YEARLY,
                        text: DASHBOARD_CHART_INTERVAL_MAPPING[DASHBOARD_CHART_INTERVAL_YEARLY],
                    });
                }
                return interval;
            },
            hideOlderThanDaysOptions() {
                return [
                    { value: null, text: 'Not Set' },
                    { value: 30, text: '30 Days' },
                    { value: 60, text: '60 Days' },
                    { value: 90, text: '90 Days' },
                    { value: 120, text: '120 Days' },
                    { value: 360, text: '360 Days' },
                ];
            },
            chartPeriodInMonthsDailyOptions() {
                return [
                    { value: 1, text: '1 Month' },
                    { value: 6, text: '6 Months' },
                    { value: 12, text: '12 Months' },
                    { value: 18, text: '18 Months' },
                    { value: 24, text: '24 Months' },
                ];
            },
            chartPeriodInMonthsMonthlyOptions() {
                return [
                    { value: 6, text: '6 Months' },
                    { value: 12, text: '12 Months' },
                    { value: 18, text: '18 Months' },
                    { value: 24, text: '24 Months' },
                ];
            },
            chartPeriodInYearsOptions() {
                return [
                    { value: 2, text: '2 Years' },
                    { value: 3, text: '3 Years' },
                    { value: 4, text: '4 Years' },
                    { value: 5, text: '5 Years' },
                    { value: 6, text: '6 Years' },
                ];
            },
            chartPeriodOptions() {
                if (this.isYearlyChartInterval) {
                    return this.chartPeriodInYearsOptions;
                } else if (this.isMonthlyChartInterval) {
                    return this.chartPeriodInMonthsMonthlyOptions;
                }
                return this.chartPeriodInMonthsDailyOptions;
            },
            tableFontSizeOptions() {
                return [
                    { value: 10, text: '10px' },
                    { value: 12, text: '12px' },
                    { value: 14, text: '14px' },
                    { value: 16, text: '16px' },
                ];
            },
            chartModuleOptions() {
                return [
                    {
                        value: DASHBOARD_CHART_MODULE_OWNERS,
                        text: DASHBOARD_CHART_MODULE_MAPPING[DASHBOARD_CHART_MODULE_OWNERS],
                    },
                    {
                        value: DASHBOARD_CHART_MODULE_QUICKBOOKS,
                        text: DASHBOARD_CHART_MODULE_MAPPING[DASHBOARD_CHART_MODULE_QUICKBOOKS],
                    },
                ];
            },
            weatherUnits() {
                return [
                    { value: weatherUnits[IMPERIAL], text: 'Fahrenheit' },
                    { value: weatherUnits[METRIC], text: 'Celsius' },
                    { value: weatherUnits[STANDARD], text: 'Kelvin' },
                ];
            },
            hasStates() {
                return this.selectedCountry === 1;
            },
            disabledSubmit() {
                if (this.withCoords) {
                    return !this.selectedCity;
                }
            },
        },
        data() {
            return {
                cities: [],
                selectedState: null,
                selectedCountry: null,
                selectedCity: null,
                cityName: null,
                countryList: [],
                loading: false,
                values: null,
                limit: 30,
                offset: 0,
                search: '',
                stateList: [],
            };
        },
        mounted() {
            this.values = this.getInitialValues();
        },
        methods: {
            getInitialValues() {
                const initialValues = {
                    settings: {
                        name: (this.initialValues.settings && this.initialValues.settings.name) || '',
                    },
                };

                if (this.withChartInterval) {
                    initialValues.settings.chart_interval =
                        this.initialValues.settings.chart_interval ||
                        this.defaultInterval ||
                        DEFAULT_DASHBOARD_CHART_INTERVAL;
                }

                if (this.withChartPeriod) {
                    initialValues.settings.chart_period =
                        this.initialValues.settings.chart_period ||
                        this.defaultPeriod ||
                        getDefaultChartPeriodBasedOnInterval(
                            this.initialValues.settings.chart_interval || DEFAULT_DASHBOARD_CHART_INTERVAL
                        );
                }

                if (this.withChartModule) {
                    initialValues.settings.chart_module =
                        this.initialValues.settings.chart_module || DEFAULT_DASHBOARD_CHART_MODULE;
                }

                if (this.withCoords) {
                    initialValues.settings.weather_unit = this.initialValues.settings.weather_unit;
                }

                if (this.withAirbnbReservations) {
                    initialValues.settings.display_airbnb_reservations =
                        this.initialValues.settings.display_airbnb_reservations;
                }

                if (this.withBlockedReservations) {
                    initialValues.settings.display_blocked_reservations =
                        this.initialValues.settings.display_blocked_reservations;
                }

                if (this.withHideOlderThanDays) {
                    initialValues.settings.hide_older_than_days = this.initialValues.settings.hide_older_than_days;
                }

                if (this.withTableFontSize) {
                    initialValues.settings.table_font_size = this.initialValues.settings.table_font_size;
                }

                return initialValues;
            },
            fetchCities: debounce(async function (value) {
                this.selectedCity = null;
                try {
                    const params = {
                        offset: this.offset,
                        limit: this.limit,
                        search: value,
                        country: this.selectedCountry,
                    };
                    if (this.hasStates) {
                        params.state = this.selectedState;
                    }
                    const { data } = await API.get(APP_CITY_LIST_ENDPOINT(), {
                        params,
                    });
                    this.cities = !data.length ? [{ name: 'Nothing Found', id: null }] : data;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch cities' });
                }
            }, 500),
            async fetchCountries() {
                try {
                    const { data } = await API.get(APP_COUNTRY_LIST_ENDPOINT());
                    this.countryList = data.map((item) => ({
                        text: item.name,
                        value: item.id,
                        ...item,
                    }));
                    this.selectedCountry = this.city?.country?.id || this.countryList[0].value;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch countries' });
                }
            },
            async fetchStates() {
                try {
                    const { data } = await API.get(APP_STATE_LIST_ENDPOINT(this.selectedCountry));
                    this.stateList = data.map((item) => ({
                        text: item.name,
                        value: item.id,
                        ...item,
                    }));
                    this.selectedState = this.city?.state?.id || this.stateList[0].value;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch state' });
                }
            },
            onStateChange() {
                this.cityName = null;
                this.selectedCity = null;
            },
            hideModal() {
                this.$bvModal.hide(this.modalId);
                if (this.withCoords) {
                    this.cities = [];
                }
            },
            async save() {
                this.loading = true;

                try {
                    await this.update(this.values);
                    this.$emit('success-update');
                    this.$nextTick(() => {
                        this.hideModal();
                    });
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({ text: generic });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);
                    }
                } finally {
                    this.loading = false;
                }
            },
            selectCity(city) {
                if (city.id) {
                    this.selectedCity = city.id;
                    this.cities = [];
                    this.values.settings.city_id = city.id;
                    this.cityName = city.name;
                }
            },
            async onCountryChange() {
                this.cityName = null;
                this.selectedCity = null;
                if (this.hasStates) {
                    await this.fetchStates();
                    this.selectedState = this.stateList[0].value;
                }
            },
            async setData() {
                if (this.withCoords) {
                    this.cityName = this.city.name;
                    this.selectedCity = this.city;
                    await this.fetchCountries();
                    if (this.hasStates) {
                        await this.fetchStates();
                    }
                }
                this.$refs.form.setValues(this.values);
            },
            onIntervalChange(interval) {
                if (this.withChartPeriod) {
                    this.values.settings.chart_period = getDefaultChartPeriodBasedOnInterval(interval);
                }
            },
        },
        watch: {
            initialValues() {
                this.values = this.getInitialValues();
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';
    @import '../../../../mixins.scss';

    .search-city-wrapper {
        position: relative;
        .city-list-wrapper {
            padding-bottom: 10px;
            background: #fff;
            position: fixed;
            max-height: 35vh;
            width: 400px;
            overflow-y: auto;
            z-index: 9999;
            border: 1px solid rgba(0, 0, 0, 0.15);
            border-radius: 4px;
            scrollbar-width: thin;
            @include scroll-bar;
            .selectable-guest-item {
                cursor: pointer;
                &:hover {
                    background: #0d47a1;
                    color: #fff;
                }
            }
        }
    }

    @include media-breakpoint-down(sm) {
        .search-city-wrapper {
            .city-list-wrapper {
                width: 300px;
            }
        }
    }

    @include media-breakpoint-down(xs) {
        .search-city-wrapper {
            .city-list-wrapper {
                width: 300px;
            }
        }
    }
</style>
